import cn from "classnames";
import { useSwiperSlide } from "swiper/react";
import { useEffect, useState } from "react";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { VideoPlayer } from "@/src/component/partial/VideoPlayer";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiComponentType, ComponentType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import { GapBreakpointModule, ImageBreakpointModule, SectionModule } from "../component";

import HeroStatsItem from "./HeroStatsItem";
import styles from "./HeroStats.module.scss";

interface HeroStatsProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.HeroStats> {}

function HeroStats({ lazy, section, slug, snapToGrid, bottomDrawerHeight, classes, searchParams, mobileHint, quality }: HeroStatsProps) {
  const videoId = `${slug}.${section.sectionId}.hero.video`;
  const heroStats: StrapiComponentType.Section.HeroStats = section.type?.[0];

  const swiperSlide = useSwiperSlide();
  const [isSectionSeen, setIsSectionSeen] = useState(swiperSlide.isActive);

  useEffect(() => {
    if (swiperSlide.isActive && !isSectionSeen) {
      setIsSectionSeen(true);
    }
  }, [swiperSlide.isActive, isSectionSeen]);

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        styledContainer: cn(styles.hero),
      }}
      lazy={lazy}
      stylePrefix="HeroStats"
      styleContainerId={heroStats.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      quality={quality}
      AfterChildComponent={
        <>
          <VideoPlayer
            lazy={lazy ?? "lazy"}
            asset={heroStats?.hero}
            videoId={videoId}
            classes={{
              container: styles.elevation,
              video: styles.video,
            }}
            poster={LoadFromCDN.imageSizeAndQuality(section.background?.asset?.data?.attributes?.url, 80, 1920)}
          />
          {CMSLogic.isImage(heroStats?.hero) && (
            <ImgBgResponsive
              className={styles.poster}
              imageUrl={LoadFromCDN.imageSizeAndQuality(heroStats?.hero?.data?.attributes?.url, 90, 1920)}
              lazy={lazy}
              prefetch={lazy === "eager"}
            />
          )}
          {heroStats.heroBreakpoints && (
            <ImageBreakpointModule
              images={heroStats.heroBreakpoints}
              preload={true}
              classes={{
                container: styles.heroBreakpoints,
              }}
              id={heroStats.id}
              mobileHint={mobileHint}
              quality={quality}
              lazy={lazy}
            />
          )}
          <div className={cn(styles.gradientOverlay, isSectionSeen && styles.animateFadeIn, !isSectionSeen && styles.hidden)}></div>
        </>
      }
    >
      <GapBreakpointModule
        id={heroStats.id}
        gapBreakpoints={heroStats.gapBreakpoints}
        className={cn(styles.inner)}
      >
        <div className={styles.statsGridContainer}>
          <div className={cn(styles.statsGrid, isSectionSeen && styles.animateFadeIn, !isSectionSeen && styles.hidden)}>
            {heroStats.stats.map((stat) => (
              <HeroStatsItem
                key={stat.id}
                stat={stat}
              />
            ))}
          </div>
        </div>
      </GapBreakpointModule>
    </SectionModule>
  );
}

export default HeroStats;
